import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Breadcrumbs,
  Link,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  Stack,
  useTheme,
  alpha,
  Paper,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Backdrop,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ArrowBack from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import InfoIcon from '@mui/icons-material/Info';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { useHistory } from "react-router-dom";
import { PartnerguestupdateProfile } from "./action/auth";
import { useDispatch, useSelector } from "react-redux";
import S3FileUpload from 'react-s3';
import AWS from 'aws-sdk';
import defaultimg from './assets/defaultimge.png';
import { useAuthToken } from "../TokenContext";
import LanguageSelector from '../LanguageSelector';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { useCredentials } from '../useCredentials';

// Enhanced theme configuration
// Enhanced theme configuration
const componentStyles = {
  container: (theme) => ({
    backgroundColor: theme.palette.mode === 'dark' 
      ? '#1a1a1a'
      : '#f8f9fa',
    minHeight: '100vh',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4)
  }),

  profileCard: (theme) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#2d2d2d' : '#FFFFFF',
    borderRadius: '20px',
    padding: theme.spacing(4),
    boxShadow: theme.palette.mode === 'dark'
      ? '0px 8px 32px rgba(0, 0, 0, 0.3)'
      : '0px 8px 32px rgba(34, 41, 47, 0.1)',
    border: `1px solid ${theme.palette.mode === 'dark' 
      ? 'rgba(255,255,255,0.1)' 
      : 'rgba(229, 231, 235, 0.5)'}`,
    position: 'relative',
    overflow: 'hidden'
  }),

  avatarContainer: (theme) => ({
    position: 'relative',
    display: 'inline-block',
    marginBottom: theme.spacing(2)
  }),

  avatar: (theme) => ({
    width: 120,
    height: 120,
    border: `4px solid ${theme.palette.background.paper}`,
    boxShadow: theme.palette.mode === 'dark'
      ? '0 8px 24px rgba(0,0,0,0.4)'
      : '0 8px 24px rgba(0,0,0,0.15)'
  }),

  // ✅ FIXED: Now properly wrapped in function that receives theme
  uploadButton: (theme) => ({
    position: 'absolute',
    bottom: 0,
    right: 0,
    backgroundColor: theme.palette.mode === 'dark' ? '#666' : '#555',
    color: 'white',
    width: 40,
    height: 40,
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' ? '#777' : '#333',
      transform: 'scale(1.1)'
    },
    transition: 'all 0.2s ease'
  }),

  editButton: (theme) => ({
    position: 'absolute',
    top: 16,
    right: 16,
    backgroundColor: alpha(theme.palette.action.active, 0.1),
    color: theme.palette.action.active,
    '&:hover': {
      backgroundColor: alpha(theme.palette.action.active, 0.2),
      transform: 'scale(1.05)'
    },
    transition: 'all 0.2s ease'
  }),

  infoSection: (theme) => ({
    backgroundColor: theme.palette.mode === 'dark' 
      ? alpha('#2d2d2d', 0.5) 
      : alpha('#f8fafc', 0.5),
    borderRadius: '16px',
    padding: theme.spacing(3),
    marginTop: theme.spacing(3),
    border: `1px solid ${theme.palette.mode === 'dark' 
      ? 'rgba(255,255,255,0.05)' 
      : 'rgba(229, 231, 235, 0.3)'}`
  }),

  infoItem: (theme) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    padding: theme.spacing(2, 0),
    borderBottom: `1px solid ${theme.palette.mode === 'dark' 
      ? 'rgba(255,255,255,0.05)' 
      : 'rgba(229, 231, 235, 0.3)'}`,
    '&:last-child': {
      borderBottom: 'none'
    }
  }),

  formField: (theme) => ({
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#ffffff',
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: theme.palette.mode === 'dark' ? '#252525' : '#f8fafc',
      },
      '&.Mui-focused': {
        backgroundColor: theme.palette.mode === 'dark' ? '#2d2d2d' : '#ffffff',
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.action.active,
          borderWidth: '2px'
        }
      }
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.action.active
    }
  }),

  primaryButton: (theme) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#666' : '#333',
    borderRadius: '12px',
    textTransform: 'none',
    fontWeight: 600,
    padding: theme.spacing(1.5, 4),
    fontSize: '1rem',
    boxShadow: theme.palette.mode === 'dark'
      ? '0px 4px 16px rgba(255, 255, 255, 0.1)'
      : '0px 4px 16px rgba(0, 0, 0, 0.2)',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' ? '#777' : '#555',
      boxShadow: theme.palette.mode === 'dark'
        ? '0px 6px 20px rgba(255, 255, 255, 0.15)'
        : '0px 6px 20px rgba(0, 0, 0, 0.3)',
      transform: 'translateY(-2px)'
    },
    '&:disabled': {
      background: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled
    },
    transition: 'all 0.3s ease'
  }),

  secondaryButton: (theme) => ({
    borderRadius: '12px',
    textTransform: 'none',
    fontWeight: 500,
    padding: theme.spacing(1.5, 4),
    fontSize: '1rem',
    borderColor: theme.palette.mode === 'dark' ? '#666' : '#333',
    color: theme.palette.mode === 'dark' ? '#fff' : '#333',
    '&:hover': {
      borderColor: theme.palette.mode === 'dark' ? '#777' : '#555',
      backgroundColor: alpha(theme.palette.action.active, 0.05),
      transform: 'translateY(-1px)'
    },
    transition: 'all 0.3s ease'
  })
};

// Styled Components
const StyledContainer = styled(Container)(({ theme }) => ({
  ...componentStyles.container(theme)
}));

const ProfileCard = styled(Card)(({ theme }) => ({
  ...componentStyles.profileCard(theme)
}));

const InfoSection = styled(Box)(({ theme }) => ({
  ...componentStyles.infoSection(theme)
}));

const InfoItem = styled(Box)(({ theme }) => ({
  ...componentStyles.infoItem(theme)
}));

// Country options
const country = [
  { value: 'usa', label: 'USA' },
  { value: 'canada', label: 'Canada' },
  { value: 'india', label: 'India' },
  { value: 'australia', label: 'Australia' },
  { value: 'united_kingdom', label: 'United Kingdom' },
  { value: 'germany', label: 'Germany' },
  { value: 'france', label: 'France' },
  { value: 'china', label: 'China' },
  { value: 'japan', label: 'Japan' },
  { value: 'brazil', label: 'Brazil' },
  { value: 'south_africa', label: 'South Africa' },
  { value: 'mexico', label: 'Mexico' },
  { value: 'italy', label: 'Italy' },
  { value: 'spain', label: 'Spain' },
  { value: 'russia', label: 'Russia' },
  { value: 'south_korea', label: 'South Korea' },
  { value: 'new_zealand', label: 'New Zealand' },
  { value: 'singapore', label: 'Singapore' },
  { value: 'united_arab_emirates', label: 'United Arab Emirates' },
  { value: 'saudi_arabia', label: 'Saudi Arabia' },
  { value: 'argentina', label: 'Argentina' },
  { value: 'netherlands', label: 'Netherlands' },
  { value: 'sweden', label: 'Sweden' },
  { value: 'switzerland', label: 'Switzerland' },
  { value: 'norway', label: 'Norway' },
  { value: 'denmark', label: 'Denmark' },
  { value: 'finland', label: 'Finland' },
  { value: 'portugal', label: 'Portugal' },
  { value: 'poland', label: 'Poland' },
  { value: 'turkey', label: 'Turkey' }
];

const countryCodes = ['+1', '+44', '+91', '+81', '+86', '+33', '+49', '+39', '+34', '+7'];

const Partnerguest = ({ currentLocale, changeLocale }) => {
  const { credentials, isLoaded, isLoading, refresh } = useCredentials();
  const config = useMemo(() => ({
    bucketName: credentials.bucketName,
    region: credentials.region,
    accessKeyId: credentials.accessKeyId,
    secretAccessKey: credentials.secretAccessKey
  }), [credentials]);

  const S3Config = useMemo(() => ({
    bucketName: credentials.bucketName,
    region: credentials.region,
    accessKeyId: credentials.accessKeyId,
    secretAccessKey: credentials.secretAccessKey
  }), [credentials]);

  const theme = useTheme();
  const token = useAuthToken();
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const { isLoggedIn, user } = useSelector(state => state.auth);

  // State Management
  const [edit, setEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [image, setImage] = useState("");
  const [authImage, setAuthImage] = useState("");
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });

  const myBucket = new AWS.S3(S3Config);

  const [num, setNum] = useState({
    country: '+1',
    phone: '',
  });

  const [state, setState] = useState({
    fname: user?.data?.[0]?.partnerguest_firstname || '',
    mname: user?.data?.[0]?.partnerguest_middlename || '',
    lname: user?.data?.[0]?.partnerguest_lastname || '',
    country: user?.data?.[0]?.partnerguest_country || '',
    about: user?.data?.[0]?.about || '',
    email: user?.data?.[0]?.email || '',
    phone: user?.data?.[0]?.phone_number || '',
    profilePicture: user?.data?.[0]?.profilePicture || ''
  });

  // Handlers
  const onInputChangenum = (event) => {
    const { id, value } = event.target;
    setNum((prevState) => ({
      ...prevState,
      [id]: value,
    }));
  };

  const onImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(URL.createObjectURL(file));
      setUploading(true);

      S3FileUpload.uploadFile(file, config)
        .then(data => {
          const location = data.key;
          setState((prevState) => ({
            ...prevState,
            profilePicture: location
          }));
          setUploading(false);
          setAlert({ open: true, message: 'Profile picture uploaded successfully!', severity: 'success' });
        })
        .catch(err => {
          setUploading(false);
          setAlert({ open: true, message: 'Failed to upload profile picture', severity: 'error' });
        });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    const { fname, mname, lname, email, country, about, profilePicture, phone } = state;

    const data = {
      partnerguest_id: user.data[0].partnerguest_id,
      partnerguest_firstname: fname,
      phone_number: phone,
      partnerguest_middlename: mname,
      partnerguest_lastname: lname,
      partnerguest_country: country,
      about: about,
      profilePicture: profilePicture,
    };

    try {
      await dispatch(PartnerguestupdateProfile(data, headers));
      setAlert({ open: true, message: 'Profile updated successfully!', severity: 'success' });
      setEdit(false);
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      setAlert({ open: true, message: 'Failed to update profile', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const onInputChange = (e) => {
    setState((prevState) => ({
      ...prevState,
      [e.target.id]: e.target.value
    }));
  };

  const onInputChangeselect = (e) => {
    setState((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value
    }));
  };

  const generatePreSignedPutUrl = () => {
    const URL_EXPIRATION_TIME = 900;
    const params = {
      Bucket: "globldataprod",
      Key: user.data[0].profilePicture,
      Expires: URL_EXPIRATION_TIME,
    };

    myBucket.getSignedUrl('getObject', params, (err, url) => {
      setAuthImage(url);
    });
  };

  useEffect(() => {
    if (user?.data?.[0]?.profile === 0) {
      setEdit(true);
    }
    if (user?.data?.[0]?.profilePicture) {
      generatePreSignedPutUrl();
    }
  }, []);

  const InfoItemComponent = ({ icon, label, value }) => (
    <InfoItem>
      <Box sx={{ color: 'action.active', mt: 0.5 }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="body1" color="text.primary">
          {value || 'Not specified'}
        </Typography>
      </Box>
    </InfoItem>
  );

  return (
    <StyledContainer maxWidth="lg">
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: 4 }}
      >
        <Link
          component="button"
          onClick={() => history.push('/')}
          underline="hover"
          sx={{
            color: 'text.secondary',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <ArrowBack sx={{ mr: 0.5 }} fontSize="small" />
          {intl.formatMessage({ id: 'home' }) || 'Home'}
        </Link>
        <Typography color="text.primary">
          {intl.formatMessage({ id: 'manageProfile' }) || 'Manage Profile'}
        </Typography>
      </Breadcrumbs>

      {/* Main Content - Centered */}
      <Grid container justifyContent="center">
        <Grid item xs={12} md={8} lg={7}>
          <ProfileCard>
            {/* Edit Button */}
            {!edit && (
              <IconButton
                sx={componentStyles.editButton(theme)}
                onClick={() => setEdit(true)}
              >
                <EditIcon />
              </IconButton>
            )}

            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
                {user?.data?.[0]?.profile === 0 
                  ? intl.formatMessage({ id: "createProfile" }) || "Create Profile"
                  : intl.formatMessage({ id: "manageProfile" }) || "Manage Profile"
                }
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {edit 
                  ? "Update your profile information"
                  : "View and manage your profile details"
                }
              </Typography>
            </Box>

            {/* Profile Avatar */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box sx={componentStyles.avatarContainer(theme)}>
                <Avatar
                  src={image || authImage || defaultimg}
                  sx={componentStyles.avatar(theme)}
                >
                  {!image && !authImage && <PersonIcon sx={{ fontSize: 60 }} />}
                </Avatar>
                {edit && (
                  <>
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="profile-upload"
                      onChange={onImageUpload}
                    />
                    <label htmlFor="profile-upload">
                      <IconButton
                        component="span"
                        sx={componentStyles.uploadButton}
                        disabled={uploading}
                      >
                        {uploading ? <CircularProgress size={20} color="inherit" /> : <PhotoCameraIcon />}
                      </IconButton>
                    </label>
                  </>
                )}
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                {state.fname} {state.lname}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Partner Guest
              </Typography>
            </Box>

            {/* Language Settings Card */}
            <Card sx={{ p: 3, borderRadius: '16px', mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <LanguageSelector currentLocale={currentLocale} changeLocale={changeLocale} />
            </Card>
       
            {/* Content */}
            {!edit ? (
              // View Mode
              <InfoSection>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  {intl.formatMessage({ id: 'basicInformation' }) || 'Basic Information'}
                </Typography>
                
                <InfoItemComponent
                  icon={<EmailIcon />}
                  label={intl.formatMessage({ id: 'email' }) || 'Email'}
                  value={state.email}
                />
                
                <InfoItemComponent
                  icon={<LocationOnIcon />}
                  label={intl.formatMessage({ id: 'country' }) || 'Country'}
                  value={state.country}
                />
                
                <InfoItemComponent
                  icon={<PhoneIcon />}
                  label={intl.formatMessage({ id: 'phone' }) || 'Phone'}
                  value={state.phone}
                />
                
                <InfoItemComponent
                  icon={<InfoIcon />}
                  label={intl.formatMessage({ id: 'about' }) || 'About'}
                  value={state.about}
                />
              </InfoSection>
            ) : (
              // Edit Mode
              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={intl.formatMessage({ id: 'firstName' }) || 'First Name'}
                      id="fname"
                      value={state.fname}
                      onChange={onInputChange}
                      required
                      sx={componentStyles.formField(theme)}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={intl.formatMessage({ id: 'middleName' }) || 'Middle Name'}
                      id="mname"
                      value={state.mname}
                      onChange={onInputChange}
                      sx={componentStyles.formField(theme)}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={intl.formatMessage({ id: 'lastName' }) || 'Last Name'}
                      id="lname"
                      value={state.lname}
                      onChange={onInputChange}
                      required
                      sx={componentStyles.formField(theme)}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <FormControl sx={{ minWidth: 120 }}>
                        <InputLabel>Code</InputLabel>
                        <Select
                          value={num.country}
                          onChange={onInputChangenum}
                          label="Code"
                          sx={componentStyles.formField(theme)}
                        >
                          {countryCodes.map((code) => (
                            <MenuItem key={code} value={code}>
                              {code}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        fullWidth
                        label={intl.formatMessage({ id: 'phoneNumber' }) || 'Phone Number'}
                        id="phone"
                        value={state.phone}
                        onChange={onInputChange}
                        required
                        sx={componentStyles.formField(theme)}
                      />
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>{intl.formatMessage({ id: 'country' }) || 'Country'}</InputLabel>
                      <Select
                        name="country"
                        value={state.country}
                        onChange={onInputChangeselect}
                        label={intl.formatMessage({ id: 'country' }) || 'Country'}
                        required
                        sx={componentStyles.formField(theme)}
                      >
                        {country.map((data, index) => (
                          <MenuItem key={index} value={data.value}>
                            {data.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label={intl.formatMessage({ id: 'about' }) || 'About'}
                      id="about"
                      value={state.about}
                      onChange={onInputChange}
                      sx={componentStyles.formField(theme)}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={2} justifyContent="center">
                      <Button
                        variant="outlined"
                        onClick={() => setEdit(false)}
                        disabled={loading}
                        sx={componentStyles.secondaryButton(theme)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                        sx={componentStyles.primaryButton(theme)}
                      >
                        {loading 
                          ? 'Saving...' 
                          : user?.data?.[0]?.profile === 0 
                            ? intl.formatMessage({ id: 'create' }) || 'Create'
                            : intl.formatMessage({ id: 'save' }) || 'Save'
                        }
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            )}
          </ProfileCard>
        </Grid>
      </Grid>

      {/* Loading Backdrop */}
      <Backdrop open={loading} sx={{ zIndex: theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </StyledContainer>
  );
};

Partnerguest.propTypes = {
  changeLocale: PropTypes.func.isRequired,
  currentLocale: PropTypes.string.isRequired,
};

export default Partnerguest;